import { useState, useEffect, useRef } from 'react';
import { startModuleTour } from './OnboardingTour';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Megaphone, Phone, Bot, CreditCard, Activity,
         User, Shield, Users, AlertTriangle, ChevronLeft, ChevronRight, LogOut,
         Sun, Moon, Gauge, Workflow } from 'lucide-react';
import api from '../api';
import { useTheme } from '../theme';

const isSuperAdmin = localStorage.getItem('es_superadmin') === 'true';
// Rol del usuario que entró (admin = dueño de la cuenta o administrador). El servidor aplica
// los permisos; aquí solo se esconde lo que ese rol no puede usar.
const rol = localStorage.getItem('rol') || 'admin';
const SOLO_ADMIN = ['/config', '/billing', '/activity', '/usuarios'];
const navBase = [
  { to: '/',          icon: LayoutDashboard, label: 'Dashboard',   id: 'nav-dashboard' },
  { to: '/campaigns', icon: Megaphone,       label: 'Campañas',    id: 'nav-campaigns' },
  { to: '/calls',     icon: Phone,           label: 'Llamadas',    id: 'nav-calls'     },
  { to: '/config',    icon: Bot,             label: 'Agente',      id: 'nav-config'    },
  { to: '/billing',   icon: CreditCard,      label: 'Facturación', id: 'nav-billing'   },
  { to: '/activity',  icon: Activity,        label: 'Actividad',   id: 'nav-activity'  },
  { to: '/usuarios',  icon: Users,           label: 'Usuarios',    id: 'nav-usuarios'  },
  { to: '/account',   icon: User,            label: 'Mi cuenta',   id: 'nav-account'   },
  ...(isSuperAdmin ? [
    { to: '/admin/live',     icon: Gauge,         label: 'En vivo',  id: 'nav-live', grupo: 'Administración' },
    { to: '/admin',          icon: Shield,        label: 'Admin',    id: 'nav-admin' },
    { to: '/admin/clientes', icon: Users,         label: 'Clientes', id: 'nav-clientes' },
    { to: '/admin/eventos',  icon: AlertTriangle, label: 'Eventos',  id: 'nav-eventos'  },
  ] : []),
].filter(it => rol === 'admin' || !SOLO_ADMIN.includes(it.to));

// El pill viaja de un ítem a otro (Liquid Glass): no late, se desliza.

export default function Layout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { theme, toggle } = useTheme();
  // Flujos de voz (IVR sin IA): el menú solo lo muestra si el superadmin lo activó en la cuenta
  const [conFlujos, setConFlujos] = useState(false);
  useEffect(() => {
    api.get('/flujos-voz/estado').then(r => setConFlujos(!!r.data.activo)).catch(() => {});
  }, []);
  const nav = conFlujos
    ? [...navBase.slice(0, 2), { to: '/flujos-voz', icon: Workflow, label: 'Flujos de voz', id: 'nav-flujos-voz' }, ...navBase.slice(2)]
    : navBase;
  const empresa   = localStorage.getItem('nombre_empresa') || 'Mi empresa';
  const navRef    = useRef(null);
  const itemRefs  = useRef([]);
  const [pillStyle, setPillStyle]   = useState({ top: 0, height: 40 });
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [collapsed, setCollapsed]   = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeIdx = nav.findIndex(n =>
    n.to === '/' ? location.pathname === '/' : location.pathname.startsWith(n.to)
  );
  const targetIdx = hoveredIdx ?? activeIdx;

  useEffect(() => {
    const el = itemRefs.current[targetIdx];
    const navEl = navRef.current;
    if (el && navEl) {
      const navTop = navEl.getBoundingClientRect().top;
      const elTop  = el.getBoundingClientRect().top;
      setPillStyle({ top: elTop - navTop, height: el.offsetHeight });
    }
  }, [targetIdx, location.pathname, collapsed]);

  const logout = () => { localStorage.clear(); navigate('/login'); };

  const sidebarWidth = collapsed ? 64 : 220;
  const showLabels   = !collapsed;

  const handleNavClick = (to) => {
    navigate(to);
    if (isMobile) setMobileOpen(false);
  };

  const usuarioNombre = localStorage.getItem('usuario_nombre') || '';
  const rolEtiqueta = { admin: 'Administrador', supervisor: 'Supervisor', consulta: 'Solo consulta' }[rol] || rol;
  const inicial = (usuarioNombre || empresa || '?').trim().charAt(0).toUpperCase();
  const compacto = collapsed && !isMobile;

  // Menu con el aspecto de InnovaChat: vidrio oscuro, items grises en 13px/600 y el activo en
  // violeta con su pastilla; abajo el bloque de usuario (avatar, nombre, salir).
  const sidebar = (
    <div style={{
      width: isMobile ? 230 : sidebarWidth,
      flexShrink: 0,
      height: isMobile ? 'auto' : '100vh',
      minHeight: isMobile ? '100vh' : undefined,
      position: isMobile ? 'relative' : 'sticky',
      top: isMobile ? undefined : 0,
      alignSelf: 'flex-start',
      background: 'var(--glass)',
      WebkitBackdropFilter: 'blur(28px) saturate(1.5)',
      backdropFilter: 'blur(28px) saturate(1.5)',
      borderRight: '1px solid var(--glass-brd)',
      boxShadow: 'inset 0 1px 0 var(--glass-hi)',
      display: 'flex', flexDirection: 'column',
      paddingTop: 16,
      overflow: 'hidden',
      transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
    }}>
      {/* Logo + tema */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding: compacto ? '0 16px' : '0 14px', marginBottom:14,
                    flexDirection: compacto ? 'column' : 'row' }}>
        <img src="/logo.png" alt="InnovaCall" style={{ width:32, height:32, borderRadius:9, objectFit:'cover', flexShrink:0 }}
          onError={e => { e.target.style.visibility='hidden'; }}/>
        {!compacto && (
          <span style={{ color:'var(--text)', fontSize:14, fontWeight:700, letterSpacing:'-0.3px', whiteSpace:'nowrap' }}>InnovaCall</span>
        )}
        <button onClick={toggle} title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'} aria-label="Cambiar tema" style={{
          marginLeft: compacto ? 0 : 'auto', width:30, height:30, borderRadius:9, border:'1px solid var(--border)',
          background:'var(--fill, rgba(255,255,255,0.04))', color:'var(--text-2)', cursor:'pointer',
          display:'grid', placeItems:'center', flexShrink:0,
        }}>
          {theme === 'dark' ? <Sun size={15}/> : <Moon size={15}/>}
        </button>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} aria-label="Cerrar menú" style={{
            background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:18, flexShrink:0,
          }}>×</button>
        )}
      </div>

      {/* Nav */}
      <nav ref={navRef} onMouseLeave={() => setHoveredIdx(null)}
           style={{ padding:'4px 6px', flex:1, position:'relative', overflowY:'auto', minHeight:0 }}>
        {/* Pastilla del item activo (viaja al pasar el mouse) */}
        <div style={{
          position:'absolute', left:6, right:6,
          top: pillStyle.top, height: pillStyle.height,
          borderRadius:11,
          background:'var(--pill-bg)',
          border:'1px solid var(--pill-brd)',
          boxShadow:'0 4px 14px var(--pill-glow), inset 0 1px 0 var(--glass-hi)',
          transition:'top 0.44s cubic-bezier(0.34,1.56,0.64,1), height 0.44s cubic-bezier(0.34,1.56,0.64,1)',
          pointerEvents:'none', zIndex:0,
        }}/>

        {nav.map((item, idx) => {
          const isActive = item.to === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.to);
          return (
            <div key={item.to}>
            {item.grupo && !compacto && (
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-4)', letterSpacing:'.04em',
                            padding:'14px 10px 6px', textTransform:'uppercase' }}>
                {item.grupo}
              </div>
            )}
            {item.grupo && compacto && (
              <div style={{ height:1, background:'var(--border)', margin:'10px 8px' }} />
            )}
            <div
              key={item.to + '_inner'}
              id={item.id}
              ref={el => itemRefs.current[idx] = el}
              onClick={() => handleNavClick(item.to)}
              onMouseEnter={() => setHoveredIdx(idx)}
              title={compacto ? item.label : ''}
              style={{
                display:'flex', alignItems:'center', gap:10,
                padding: compacto ? '9px 0' : '9px 10px',
                justifyContent: compacto ? 'center' : 'flex-start',
                borderRadius:11, marginBottom:2, border:'1px solid transparent',
                cursor:'pointer', position:'relative', zIndex:1,
                color: isActive ? 'var(--accent)' : hoveredIdx===idx ? 'var(--text)' : 'var(--text-3)',
                fontWeight:600, fontSize:13,
                transition:'color 0.2s', userSelect:'none',
              }}
            >
              <item.icon size={16} strokeWidth={2} style={{ minWidth:18, flexShrink:0 }} />
              {!compacto && <span style={{ whiteSpace:'nowrap' }}>{item.label}</span>}
            </div>
            </div>
          );
        })}
      </nav>

      {/* Usuario (como InnovaChat): avatar, nombre, rol y salir */}
      <div style={{ borderTop:'1px solid var(--border)', padding: compacto ? '10px 0' : '10px 12px',
                    display:'flex', alignItems:'center', gap:10, flexDirection: compacto ? 'column' : 'row' }}>
        <div title={compacto ? `${usuarioNombre || empresa}` : ''} style={{
          width:28, height:28, borderRadius:'50%', flexShrink:0,
          background:'linear-gradient(135deg,var(--accent-solid),var(--teal))',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff',
        }}>{inicial}</div>
        {!compacto && (
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12.5, fontWeight:600, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {usuarioNombre || empresa}
            </div>
            <div style={{ fontSize:11, color:'var(--text-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {usuarioNombre ? `${empresa} · ${rolEtiqueta}` : 'Administrador'}
            </div>
          </div>
        )}
        <button onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión" style={{
          width:28, height:28, borderRadius:7, border:'none', background:'transparent', color:'var(--text-3)',
          cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
        }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)'; }}>
          <LogOut size={15}/>
        </button>
        {!isMobile && (
          <button onClick={() => setCollapsed(p => !p)} title={collapsed ? 'Expandir menú' : 'Contraer menú'} aria-label="Contraer menú" style={{
            width:28, height:28, borderRadius:7, border:'none', background:'transparent', color:'var(--text-3)',
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
          }}>
            {collapsed ? <ChevronRight size={15}/> : <ChevronLeft size={15}/>}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'transparent', position:'relative', zIndex:1 }}>

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)', zIndex:40,
        }}/>
      )}

      {/* Sidebar desktop */}
      {!isMobile && sidebar}

      {/* Sidebar mobile — drawer */}
      {isMobile && mobileOpen && (
        <div style={{ position:'fixed', left:0, top:0, bottom:0, zIndex:50 }}>
          {sidebar}
        </div>
      )}

      <HelpButton navigate={navigate} location={location} />
      {/* Content */}
      <div style={{ flex:1, overflow:'auto', padding: isMobile ? '16px' : '32px', minWidth:0 }}>
        {/* Mobile top bar */}
        {isMobile && (
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <button onClick={() => setMobileOpen(true)} style={{
              width:38, height:38, borderRadius:9,
              background:'var(--glass)', border:'1px solid var(--border)',
              color:'var(--text-2)', cursor:'pointer', fontSize:18, display:'flex',
              alignItems:'center', justifyContent:'center', flexShrink:0,
            }}>☰</button>
            <div style={{ fontFamily:'Inter,sans-serif', fontWeight:700, fontSize:14, color:'var(--text)' }}>InnovaCall</div>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}

function HelpButton({ navigate, location }) {
  const moduleMap = {
    '/':           'dashboard',
    '/campaigns':  'campaigns',
    '/calls':      'calls',
    '/config':     'config',
    '/billing':    'billing',
  };
  const moduleId = Object.entries(moduleMap).find(([path]) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
  )?.[1];

  if (!moduleId) return null;

  return (
    <button
      onClick={() => startModuleTour(moduleId, navigate)}
      title="Guía de este módulo"
      style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 8000,
        width: 42, height: 42, borderRadius: '50%',
        background: 'var(--accent-solid)',
        border: 'none', color: '#fff', fontSize: 18, fontWeight: 700,
        cursor: 'pointer', boxShadow: '0 4px 20px rgba(124,58,237,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(124,58,237,0.7)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(124,58,237,0.5)'; }}
    >?</button>
  );
}
